import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { timestampToDateInput, dateInputToTimestamp } from '../utils/formatters';
import { useT } from '../i18n';

// A date field with typed entry plus our own compact picker.
//
// The native <input type="date"> popup is deliberately suppressed: browsers
// render the year as a long scrolling list, so reaching the far end of this
// widget's ~10-year range takes a lot of imprecise scrolling — the same
// complaint that made the range slider unusable for short windows. Ours puts
// the whole range on screen at once: a year select over a 12-cell month grid
// over the days, so any date is two or three clicks away.
//
// Typing is unchanged — the input stays a real date field, so the browser
// still renders it in the viewer's locale (TT.MM.JJJJ here) and arrow keys
// still step the focused segment.
//
// Month and weekday abbreviations come from the dictionary (dateField).

const Container = styled.div<{ isOpen: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  /* Local to FilterCard's stacking context — the open calendar overlaps its
     siblings, same approach as SearchableSelect. */
  z-index: ${p => (p.isOpen ? 3 : 2)};
`;

const Input = styled.input`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  font-weight: 400;
  color: var(--color-text-default);
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 2px 22px 2px 4px;
  /* A native date field carries an intrinsic width that ignores its grid
     column; without an explicit width + min-width:0 it pushes FilterGrid
     past the card edge. */
  width: 7.5em;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: var(--color-data-700);
  }

  /* Suppress the browser's own picker — ours replaces it. */
  &::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
`;

const CalendarButton = styled.button`
  position: absolute;
  right: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.muted};
  line-height: 1;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: var(--color-text-default);
  }
`;

const Popover = styled.div<{ shift: number }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  /* Anchored to its field, then nudged back inside the viewport — see the
     clamp in Calendar. The "bis" field sits far enough right that a fixed
     alignment overflows one edge or the other depending on widget width. */
  transform: translateX(${p => p.shift}px);
  width: 244px;
  max-width: calc(100vw - 32px);
  padding: 8px;
  background: #ffffff;
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-m);
  z-index: 9;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

// A native <select> hands its popup to the browser, which sizes it to the
// option count — 11 years render as one tall unscrollable block. This is our
// own menu so the list keeps a fixed height and scrolls inside it.
const YearAnchor = styled.div`
  position: relative;
`;

const YearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  color: var(--color-text-default);
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 2px 6px;
  cursor: pointer;

  &:hover { border-color: var(--color-data-700); }

  &::after {
    content: '';
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    border-top: 4px solid currentColor;
  }
`;

const YearMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  /* Fixed height — roughly five rows — with the rest scrolled. Setting only
     overflow-y makes CSS compute overflow-x to auto as well, which gave the
     list a pointless sideways scroll; the years never need one. */
  max-height: 140px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  /* The platform scrollbar is a heavy thing next to a list this narrow.
     Chromium/WebKit take the 5px bar below; Firefox has no scrollbar
     pseudo-elements, so it gets the standard property instead — scoped,
     because where both are understood the standard one wins and would
     substitute the browser's own (wider) "thin" bar for ours. */
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: rgba(24, 28, 68, 0.25) transparent;
  }

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(24, 28, 68, 0.25);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(24, 28, 68, 0.45);
  }
  padding: 2px;
  background: #ffffff;
  border-radius: ${theme.borderRadius.sm};
  box-shadow: var(--shadow-m);
  z-index: 2;
`;

const YearOption = styled.button<{ selected: boolean }>`
  display: block;
  width: 100%;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
  text-align: left;
  padding: 4px 8px;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${p => (p.selected ? '#ffffff' : 'var(--color-text-default)')};
  background: ${p => (p.selected ? 'var(--color-data-700)' : 'transparent')};

  &:hover {
    background: ${p => (p.selected ? 'var(--color-data-700)' : 'rgba(24, 28, 68, 0.08)')};
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  color: ${theme.colors.text.muted};
  &:hover { color: var(--color-text-default); }
`;

const Grid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.columns}, 1fr);
  gap: 2px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
  margin: 6px 0;
`;

const Cell = styled.button<{ selected?: boolean }>`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  padding: 4px 0;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${p => (p.selected ? '#ffffff' : 'var(--color-text-default)')};
  background: ${p => (p.selected ? 'var(--color-data-700)' : 'transparent')};

  &:hover:not(:disabled) {
    background: ${p => (p.selected ? 'var(--color-data-700)' : 'rgba(24, 28, 68, 0.08)')};
  }

  &:disabled {
    color: ${theme.colors.text.muted};
    opacity: 0.35;
    cursor: default;
  }
`;

const WeekdayLabel = styled.span`
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  text-align: center;
  padding: 2px 0;
`;

// Calendar math runs in UTC throughout, matching the timestamps the widget
// filters by (see utils/formatters).
const utc = (y: number, m: number, d: number) => Date.UTC(y, m, d);
const daysInMonth = (y: number, m: number) => new Date(utc(y, m + 1, 0)).getUTCDate();
/** Monday-first weekday index of the 1st, i.e. how many blanks the grid needs. */
const leadingBlanks = (y: number, m: number) => (new Date(utc(y, m, 1)).getUTCDay() + 6) % 7;

interface CalendarProps {
  value: number;
  min: number;
  max: number;
  onSelect: (ts: number) => void;
  onClose: () => void;
}

function Calendar({ value, min, max, onSelect, onClose }: CalendarProps) {
  const m = useT();
  const selected = new Date(value);
  const [viewYear, setViewYear] = useState(selected.getUTCFullYear());
  const [viewMonth, setViewMonth] = useState(selected.getUTCMonth());
  const popoverRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);
  const selectedYearRef = useRef<HTMLButtonElement>(null);
  const [shift, setShift] = useState(0);
  const [yearMenuOpen, setYearMenuOpen] = useState(false);

  // Keep the popover on screen. Measure the unshifted box (subtract the shift
  // already applied) so this converges on the first correction instead of
  // chasing its own offset.
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    const left = rect.left - shift;
    const right = rect.right - shift;
    let next = 0;
    if (right > window.innerWidth - margin) next = window.innerWidth - margin - right;
    if (left + next < margin) next = margin - left;
    if (next !== shift) setShift(next);
  }, [shift, viewYear, viewMonth]);

  // Open on the current year rather than at the top of the list.
  useLayoutEffect(() => {
    if (yearMenuOpen) selectedYearRef.current?.scrollIntoView({ block: 'center' });
  }, [yearMenuOpen]);

  // The field's own outside-click handler only sees clicks outside the whole
  // popover; this closes the menu when the click lands on the calendar behind it.
  useEffect(() => {
    if (!yearMenuOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (yearRef.current && !yearRef.current.contains(event.target as Node)) {
        setYearMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [yearMenuOpen]);

  const years = useMemo(() => {
    const first = new Date(min).getUTCFullYear();
    const last = new Date(max).getUTCFullYear();
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [min, max]);

  const days = useMemo(() => {
    const blanks = leadingBlanks(viewYear, viewMonth);
    const count = daysInMonth(viewYear, viewMonth);
    return [
      ...Array.from({ length: blanks }, () => null),
      ...Array.from({ length: count }, (_, i) => i + 1),
    ];
  }, [viewYear, viewMonth]);

  return (
    <Popover ref={popoverRef} shift={shift} role="dialog" aria-label={m.dateField.chooseDate}>
      <PopoverHeader>
        <YearAnchor ref={yearRef}>
          <YearButton
            type="button"
            aria-label={m.dateField.year}
            aria-haspopup="listbox"
            aria-expanded={yearMenuOpen}
            onClick={() => setYearMenuOpen(o => !o)}
          >
            {viewYear}
          </YearButton>
          {yearMenuOpen && (
            <YearMenu role="listbox" aria-label={m.dateField.year}>
              {years.map(y => (
                <YearOption
                  key={y}
                  ref={y === viewYear ? selectedYearRef : undefined}
                  type="button"
                  role="option"
                  aria-selected={y === viewYear}
                  selected={y === viewYear}
                  onClick={() => { setViewYear(y); setYearMenuOpen(false); }}
                >
                  {y}
                </YearOption>
              ))}
            </YearMenu>
          )}
        </YearAnchor>
        <CloseButton type="button" onClick={onClose} aria-label={m.common.close}>×</CloseButton>
      </PopoverHeader>

      <Grid columns={4}>
        {m.dateField.months.map((label, month) => (
          <Cell
            key={label}
            type="button"
            // A month is reachable only if any of its days falls in range.
            disabled={utc(viewYear, month + 1, 0) < min || utc(viewYear, month, 1) > max}
            selected={month === viewMonth}
            onClick={() => setViewMonth(month)}
          >
            {label}
          </Cell>
        ))}
      </Grid>

      <Divider />

      <Grid columns={7}>
        {m.dateField.weekdays.map(d => <WeekdayLabel key={d}>{d}</WeekdayLabel>)}
        {days.map((day, i) => {
          if (day == null) return <span key={`blank-${i}`} />;
          const ts = utc(viewYear, viewMonth, day);
          return (
            <Cell
              key={day}
              type="button"
              disabled={ts < min || ts > max}
              selected={ts === value}
              onClick={() => onSelect(ts)}
            >
              {day}
            </Cell>
          );
        })}
      </Grid>
    </Popover>
  );
}

interface DateFieldProps {
  ariaLabel: string;
  /** The applied timestamp; the field shows this whenever it isn't being typed into. */
  value: number;
  min: number;
  max: number;
  onChange: (ts: number) => void;
}

export function DateField({ ariaLabel, value, min, max, onChange }: DateFieldProps) {
  const m = useT();
  // What the field shows while it is being typed into. A half-typed date
  // ("2026-03-0") parses to nothing, and a controlled field bound straight to
  // `value` would snap it back mid-entry. Undefined means "show the applied
  // value" — which is also where an applied change lands us.
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setDraft(undefined); }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const handleInput = (next: string) => {
    setDraft(next);
    const ts = dateInputToTimestamp(next);
    // Incomplete or outside the data — hold it as a draft and apply nothing.
    // onBlur drops it, so the field never shows a range that isn't filtered.
    if (ts == null || ts < min || ts > max) return;
    onChange(ts);
  };

  return (
    <Container ref={containerRef} isOpen={isOpen}>
      <Input
        type="date"
        aria-label={ariaLabel}
        value={draft ?? timestampToDateInput(value)}
        min={timestampToDateInput(min)}
        max={timestampToDateInput(max)}
        onChange={e => handleInput(e.target.value)}
        onBlur={() => setDraft(undefined)}
      />
      <CalendarButton
        type="button"
        aria-label={m.dateField.openCalendar(ariaLabel)}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(o => !o)}
      >
        {/* Inline SVG rather than the shared-styles icon font: the host page
            supplies that sheet and it carries no calendar glyph today. */}
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <rect x="1.7" y="3" width="12.6" height="11.3" rx="1.6" />
          <path d="M1.7 6.6h12.6M5.2 1.6v2.6M10.8 1.6v2.6" />
        </svg>
      </CalendarButton>
      {isOpen && (
        <Calendar
          value={value}
          min={min}
          max={max}
          onSelect={ts => { onChange(ts); setIsOpen(false); }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </Container>
  );
}
